import React, { useState } from 'react'
import { Form, Row, Col, Input, Typography, Select, Button, Result, Space, notification } from 'antd'

import DeductionService from '../../service/DeductionService'
import { DeductionCreatePayload, DeductionRetrieve } from '../../domain/Deduction'
import { ModeContext } from '../../provider/ModeProvider'
import { useNavigate } from 'react-router-dom'
import CurrencyService from '../../service/CurrencyService'

const { Title } = Typography

const currencies: string[] = CurrencyService.allCurrencies
const most_used_currencies: string[] = CurrencyService.mostUsedCurrencies

const DeductiionCreateForm: React.FC = () => {
    const { setDeductionMode } = ModeContext()
    const [form] = Form.useForm()
    const [transaction, setTransaction] = useState<string | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [hideReference, setHideReference] = useState<boolean>(true)
    const [apiNotification, contextNotification] = notification.useNotification()

    const navigate = useNavigate()
    const redirect = (url: string) => {
        navigate(url)
        setDeductionMode('')
    }

    const onFinish = () => {
        const values: DeductionCreatePayload = form.getFieldsValue() as DeductionCreatePayload
        if (values.referenceType === 'none') values.reference = values.remarks
        setLoading(true)
        DeductionService.create(values)
            .then((result: DeductionRetrieve) => {
                setTransaction(result.id)
            })
            .catch((e) => {
                apiNotification.open({
                    message: 'Simulate Deduction Failed',
                    description: 'Something went wrong. Please try again later. If the issue keep happening, please contact Customer Support',
                    placement: 'top',
                })
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const createDeduction = () => {
        reset()
        setTransaction(null)
    }

    const reset = () => {
        form.resetFields()
    }

    const prefixSelector = (
        <Form.Item
            name="currencyId"
            rules={[
                {
                    required: true,
                    message: 'Please input deduction Currency',
                },
            ]}
            noStyle
        >
            <Select
                placeholder="Currency"
                options={[
                    {
                        label: 'Most Used',
                        options: most_used_currencies.map((currency) => ({ label: currency, value: currency })),
                    },
                    {
                        label: 'Others',
                        options: currencies.map((currency) => ({ label: currency, value: currency })),
                    },
                ]}
            ></Select>
        </Form.Item>
    )

    const deductionTypeOptions = [
        { value: 'adjustment', label: 'Adjustment' },
        { value: 'refund_to_client_bank_account', label: 'Refund to client bank account' },
        { value: 'bank_fees_inward_credit', label: 'Bank Fees (Inward Credit)' },
        { value: 'bank_fees_others', label: 'Bank Fees (Others)' },
        { value: 'wallex_fees', label: 'Wallex Fees' },
        { value: 'wallex_fees_amendment_charge', label: 'Wallex Fees (Amendment Charge)' },
        { value: 'wallex_fees_change_in_payment_terms', label: 'Wallex Fees (Change in Payment Terms)' },
        { value: 'wallex_fees_fx_losses', label: 'Wallex Fees (FX Losses)' },
        { value: 'wallex_fees_late_funding', label: 'Wallex Fees (Late Funding)' },
        { value: 'wallex_fees_negative_interest_charge', label: 'Wallex Fees (Negative Interest Charge)' },
        { value: 'wallex_fees_payment_cancellation', label: 'Wallex Fees (Payment Cancellation)' },
        { value: 'wallex_fees_recall_charge', label: 'Wallex Fees (Recall Charge)' },
        { value: 'wallex_fees_tracer_charge', label: 'Wallex Fees (Tracer Charge)' },
        { value: 'others', label: 'Other' },
    ]

    const referenceTypeOptions = [
        { value: 'none', label: 'None' },
        { value: 'funding', label: 'Funding' },
        { value: 'payment', label: 'Payment' },
        { value: 'conversion', label: 'Conversion' },
        { value: 'collection', label: 'Collection' },
    ]

    return (
        <>
            {contextNotification}
            {transaction && (
                <Result
                    status="success"
                    title="Create Deduction Success"
                    subTitle={`Your Funding ID is ${transaction}`}
                    extra={[
                        <Button type="primary" key="console" onClick={createDeduction}>
                            Create Another Deduction
                        </Button>,
                        <Button htmlType="submit" onClick={() => redirect(`/deduction?id=${transaction}`)}>
                            Update Deduction Status
                        </Button>,
                    ]}
                />
            )}

            {!transaction && (
                <Form name="deduction-create" layout="vertical" onFinish={onFinish} form={form} initialValues={{ referenceType: 'none' }}>
                    <Row>
                        <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 8 }}>
                            <Title level={5}>Deduction Information</Title>
                            <Form.Item
                                label="User ID"
                                name="userId"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input User Id',
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="amount"
                                label="Deduction Amount"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input Funding Amount',
                                    },
                                ]}
                            >
                                <Input addonAfter={prefixSelector} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item
                                name="deductionType"
                                label="Deduction Type"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input deduction type',
                                    },
                                ]}
                            >
                                <Select placeholder="Deduction Type" options={deductionTypeOptions}></Select>
                            </Form.Item>
                            <Form.Item name="referenceType" label="Reference Type">
                                <Select
                                    placeholder="none"
                                    options={referenceTypeOptions}
                                    onChange={(e: string) => {
                                        e === 'none' ? setHideReference(true) : setHideReference(false)
                                    }}
                                ></Select>
                            </Form.Item>
                            {!hideReference && (
                                <Form.Item
                                    label="Reference"
                                    name="reference"
                                    rules={[
                                        {
                                            required: true,
                                            message: `Please input Reference`,
                                        },
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            )}
                            <Form.Item
                                label="Remarks"
                                name="remarks"
                                rules={[
                                    {
                                        required: true,
                                        message: `Please input remarks`,
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item label=" " colon={false}>
                                <Space>
                                    <Button type="primary" htmlType="submit" loading={loading}>
                                        Submit
                                    </Button>
                                    <Button htmlType="submit" onClick={reset}>
                                        Reset
                                    </Button>
                                    <Button htmlType="submit" onClick={() => setDeductionMode('')}>
                                        Back
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            )}
        </>
    )
}

export default DeductiionCreateForm
